/**
 * Media API resource
 * Endpoint: /api/easyblocks/media
 */

import { getApiBaseUrl, joinUrl } from "./client";
import type { MediaListParams, MediaListResponse, MediaUploadResponse } from "./types";

/**
 * GET /api/easyblocks/media?path={path}&q={q}
 * List media files and folders
 */
export async function listMedia(params: MediaListParams): Promise<MediaListResponse> {
  const url = new URL(joinUrl(getApiBaseUrl(), "media"), window.location.origin);
  if (params.path) url.searchParams.set("path", params.path);
  if (params.q) url.searchParams.set("q", params.q);

  const res = await fetch(url.toString(), {
    method: "GET",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Media list failed (${res.status} ${res.statusText}): ${text}`);
  }

  return (await res.json()) as MediaListResponse;
}

/**
 * POST /api/easyblocks/media
 * Upload a media file
 */
export async function uploadMedia(
  file: File,
  path?: string,
  uploadContext?: string
): Promise<MediaUploadResponse> {
  const fd = new FormData();
  fd.append("file", file);
  if (path) {
    fd.append("path", path);
  }
  if (uploadContext) {
    fd.append("upload_context", uploadContext);
  }

  const res = await fetch(joinUrl(getApiBaseUrl(), "media"), {
    method: "POST",
    body: fd,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Media upload failed (${res.status} ${res.statusText}): ${text}`);
  }

  return (await res.json()) as MediaUploadResponse;
}

/**
 * Convert file to data URL (for local fallback)
 */
export async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
